/** @jsx jsx */
import type { ReactElement, ReactNode } from 'react';
import { jsx } from '@balance-web/core';
import { Heading } from '@balance-web/heading';
import { Stack } from '@balance-web/stack';
import { forwardRefWithAs, makeId, useId } from '@balance-web/utils';
import { Text } from '@balance-web/text';
import { Box } from '@balance-web/box';

import type { ModalSize } from './size';
import { useModalWidth } from './size';
import { DialogBase } from './DialogBase';

type PassiveDialogProps = {
  /** Use any of our spot or hero illustrations here. */
  illustration?: ReactElement;
  children: ReactNode;
  title?: string;
  id?: string;
  isOpen: boolean;
  size?: ModalSize;
};

export const PassiveDialog = forwardRefWithAs<'div', PassiveDialogProps>(
  (
    { children, illustration, title, id, isOpen, size = 'small', ...props },
    ref
  ) => {
    const instanceId = useId(id);
    const headingId = makeId(instanceId, 'heading');

    const modalWidth = useModalWidth(size);

    return (
      <DialogBase
        ref={ref}
        aria-labelledby={headingId}
        autoFocus={false}
        id={instanceId}
        isOpen={isOpen}
        width={modalWidth}
        onClose={() => {}}
        {...props}
      >
        <Stack
          align="center"
          css={{
            textAlign: 'center',
          }}
          paddingBottom="xxlarge"
        >
          {illustration ? (
            <Box paddingBottom="large" paddingTop="xlarge">
              {illustration}
            </Box>
          ) : null}
          <Stack gap="medium" paddingTop="large">
            {title ? (
              <Heading as="h2" id={headingId} level="3">
                {title}
              </Heading>
            ) : null}
            <Text color="textPrimary">{children}</Text>
          </Stack>
        </Stack>
      </DialogBase>
    );
  }
);
